import { File05 } from '@untitled-ui/icons-react/build/cjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import CellDropdown from 'src/components/CellDropdown';
import TooltipCmp from 'src/components/TooltipCmp';
import V3DropdownInfo from 'src/components/V3DropdownCmp/V3DropdownInfo';

import { buildScheduleDateRange, buildScheduleDisplay, hasScheduleTime, IBusinessHours, isBusinessHoursWindow } from './legOverview.const';

export interface IInstructionEntry {
    label: string;
    text: string;
}

interface IProps {
    schedule: any;
    type: 'pickup' | 'delivery';
    /** Card heading behind the chevron, e.g. "Pickup Time". */
    heading: string;
    /** Card heading behind the note icon, e.g. "Pickup Instructions". */
    instructionTitle: string;
    instruction?: string | null;
    /** Engagement summary rows: one labelled entry per leg of the run; takes precedence over `instruction`. */
    instructionEntries?: IInstructionEntry[];
    /** Shipper (pickup) / consignee (delivery) business hours — a Business Hours window has no time of its own. */
    businessHours?: IBusinessHours;
}

/**
 * Pickup / Delivery Time cell (port of WALHQ's Route Overview cell): the date alone is the visible
 * text, the full "date @ time" sits behind a chevron dropdown (when a time exists OR the window is
 * Business Hours, filled from the entity's hours), and the leg's instruction sits behind a trailing
 * note icon. The icon is a sibling OUTSIDE the dropdown on purpose (same as WALHQ): nested inside, it
 * disappeared on every stop without a time — which is why inner stops showed no instruction.
 */
const ScheduleTimeCell = ({ schedule, type, heading, instructionTitle, instruction, instructionEntries = [], businessHours }: IProps) => {
    const { t } = useTranslation();
    const hasDropdown = hasScheduleTime(schedule, type);
    const hasInstruction = instructionEntries.length > 0 || !!instruction;

    const scheduleText = buildScheduleDisplay(schedule, type, businessHours);
    const scheduleInfo = isBusinessHoursWindow(schedule, type) ? `${scheduleText} · ${t('Common.Business_Hours')}` : scheduleText;

    const instructionInfo = instructionEntries.length ? (
        <>
            {instructionEntries.map((entry, index) => (
                <span key={`${entry.label}-${index}`} className="block mt-1 first:mt-0">
                    <span className="font-medium text-grayLight900">{entry.label}</span>
                    {`: ${entry.text}`}
                </span>
            ))}
        </>
    ) : (
        instruction
    );

    // Schedule strings come from our own formatter (ordinal <sup> markup only) — never from user input.
    const dateContent = (
        <div className="w-full overflow-hidden">
            <p className="truncate" dangerouslySetInnerHTML={{ __html: buildScheduleDateRange(schedule, type) }} />
        </div>
    );

    // The grey hover fill must reach the true cell edge with only the text inset, so the side padding
    // lives on the date box (left) and on whichever element is last in the row (right).
    const leftEdge = 'pl-[var(--table-col-space-common-xs)]';
    const rightEdge = hasInstruction ? '' : 'pr-[var(--table-col-space-common-xs)]';

    return (
        <div className="flex items-center gap-x-1 h-full">
            {/* items-stretch so CellDropdown's hover background gets a height through the whole chain. */}
            <div className="flex flex-1 min-w-0 items-stretch self-stretch overflow-hidden">
                {hasDropdown ? (
                    <div className="w-full">
                        <CellDropdown
                            isCompactTrigger
                            wrapperClassName={`flex items-center gap-x-1 h-full w-full hover:bg-utilityGray200 ${leftEdge} ${rightEdge}`}
                            message={<V3DropdownInfo heading={heading} info={<span dangerouslySetInnerHTML={{ __html: scheduleInfo }} />} />}
                        >
                            {dateContent}
                        </CellDropdown>
                    </div>
                ) : (
                    <div className={`flex h-full w-full items-center overflow-hidden ${leftEdge} ${rightEdge}`}>{dateContent}</div>
                )}
            </div>
            {/* Trailing sibling (not inside the dropdown) so the icon sits in the same spot on every row, chevron or not. */}
            {hasInstruction ? (
                <div onClick={(e) => e.stopPropagation()} className="flex-none pr-[var(--table-col-space-common-xs)]">
                    <TooltipCmp TooltipCustomClass="tooltipV3-content-wrap" isArrow position="top" message={<V3DropdownInfo heading={instructionTitle} info={instructionInfo} />}>
                        <File05 className="w-4 h-4 text-gray500 cursor-pointer flex-none -scale-x-100 rotate-180" />
                    </TooltipCmp>
                </div>
            ) : null}
        </div>
    );
};

export default ScheduleTimeCell;
